import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { ScreenHeader } from '../../components/common';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  findTerm,
  searchTerms,
  type GlossaryCategory,
  type GlossaryTerm,
} from './terms';

/**
 * The "explain everything" glossary.
 *
 * Deep-linkable: `/glossary#nakshatra` opens, scrolls to and briefly highlights
 * that term, so a TermSheet, a chart fact or an onboarding footnote can point
 * straight at the entry it means. The hash also accepts aliases and engine
 * fact ids (`/glossary#jyotish.lagna`), resolved through findTerm.
 */
export function GlossaryScreen() {
  const { t } = useI18n();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set<string>());
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const targetId = useMemo(() => {
    const raw = decodeURIComponent(location.hash.replace(/^#/, ''));
    return raw ? (findTerm(raw)?.id ?? null) : null;
  }, [location.hash]);

  useEffect(() => {
    if (!targetId) return;
    // A live search would otherwise filter the deep-linked term out of view.
    setQuery('');
    setExpanded((prev) => new Set(prev).add(targetId));
    setHighlighted(targetId);
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(`term-${targetId}`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    const timer = window.setTimeout(() => setHighlighted(null), 2600);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [targetId]);

  const results = useMemo(() => searchTerms(query), [query]);

  const groups = useMemo(() => {
    const byCategory = new Map<GlossaryCategory, GlossaryTerm[]>();
    for (const term of results) {
      const list = byCategory.get(term.category);
      if (list) list.push(term);
      else byCategory.set(term.category, [term]);
    }
    return CATEGORY_ORDER.map((category) => ({
      category,
      terms: byCategory.get(category) ?? [],
    })).filter((group) => group.terms.length > 0);
  }, [results]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="screen">
      <ScreenHeader title={t('glossary.title')} subtitle={t('glossary.subtitle')} back />

      {/* The curriculum is the guided way in; this screen is the reference. */}
      <Link to="/learn" style={{ color: 'inherit', display: 'block', marginBottom: 12 }}>
        <div className="banner banner--info">{t('glossary.startCurriculum')} →</div>
      </Link>

      <input
        className="input"
        type="search"
        value={query}
        aria-label={t('glossary.search')}
        placeholder={t('glossary.search')}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlighted(null);
        }}
      />

      {groups.length === 0 && (
        <div className="card muted small" style={{ marginTop: 16, lineHeight: 1.6 }}>
          {t('glossary.empty')}
        </div>
      )}

      {groups.map((group) => (
        <section key={group.category}>
          <div className="section-title">{CATEGORY_LABELS[group.category]}</div>
          <div className="col">
            {group.terms.map((term) => {
              const isOpen = expanded.has(term.id);
              const isHit = highlighted === term.id;
              return (
                <div
                  key={term.id}
                  id={`term-${term.id}`}
                  className={`card${isHit ? ' card--strong' : ''}`}
                  style={{
                    padding: 0,
                    overflow: 'hidden',
                    borderColor: isHit ? 'var(--accent)' : undefined,
                    boxShadow: isHit ? '0 0 0 3px var(--accent-soft)' : undefined,
                    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
                  }}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`term-body-${term.id}`}
                    onClick={() => toggle(term.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      width: '100%',
                      minHeight: 44,
                      padding: 14,
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text)',
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      <span className="h3" style={{ display: 'block' }}>
                        {term.title}
                      </span>
                      <span className="small muted" style={{ display: 'block', marginTop: 3 }}>
                        {term.short}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className="faint"
                      style={{
                        flexShrink: 0,
                        lineHeight: 1.4,
                        transform: isOpen ? 'rotate(180deg)' : undefined,
                        transition: 'transform 0.18s ease',
                      }}
                    >
                      ▾
                    </span>
                  </button>

                  {isOpen && (
                    <div
                      id={`term-body-${term.id}`}
                      className="small muted fade-in"
                      style={{
                        padding: '0 14px 14px',
                        lineHeight: 1.65,
                        borderTop: '1px solid var(--border)',
                        paddingTop: 12,
                      }}
                    >
                      {term.long}
                      {term.aliases && term.aliases.length > 0 && (
                        // Other spellings people search for. Deliberately
                        // unlabelled — a label here would be untranslated copy.
                        <div className="tiny faint" style={{ marginTop: 10 }}>
                          {term.aliases.slice(0, 5).join(' · ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
